import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  /** Optional label used in the fallback copy (e.g. "3D scene"). */
  scope?: string;
};

type State = {
  hasError: boolean;
  message?: string;
};

/**
 * Catches runtime crashes anywhere in the React tree and shows a recoverable
 * fallback instead of a blank white screen. The 3D stack (three.js, spark,
 * react-three-fiber) can throw asynchronously on unusual hardware, after a lost
 * WebGL context, or when a scene asset is malformed — without a boundary the
 * whole game would unmount and the player would be stuck.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    const message =
      error instanceof Error ? error.message : typeof error === "string" ? error : "Unexpected error";
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    // Surface for diagnostics without breaking the render path.
    // eslint-disable-next-line no-console
    console.error("Runtime error captured by ErrorBoundary", error, info?.componentStack);
  }

  private handleReload = () => {
    // A hard reload is the most reliable recovery for a corrupted GL/asset state.
    try {
      window.location.reload();
    } catch {
      this.setState({ hasError: false, message: undefined });
    }
  };

  private handleDismiss = () => {
    this.setState({ hasError: false, message: undefined });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const scope = this.props.scope ? ` (${this.props.scope})` : "";
    return (
      <div className="error-boundary" role="alert" aria-live="assertive">
        <div className="error-boundary__panel ui-panel">
          <strong>Something went sideways{scope}</strong>
          <p>
            The flight session hit an unexpected error. Your progress this run may be lost, but a
            reload usually clears it.
          </p>
          {this.state.message ? (
            <code className="error-boundary__detail">{this.state.message}</code>
          ) : null}
          <div className="error-boundary__actions">
            <button type="button" onClick={this.handleReload}>
              Reload game
            </button>
            <button type="button" className="error-boundary__ghost" onClick={this.handleDismiss}>
              Try to continue
            </button>
          </div>
        </div>
      </div>
    );
  }
}
